import type { ThemePackChoice, ThemePackId, ThemePackScope } from '@dijitalapp/shared';
import type { CSSProperties } from 'react';

/*
 * Theme packs (the Pro Market app "Tema Paketleri"): each one a designed set of light and dark colours for the
 * app's surfaces and text, the chat bubbles, and a drawn wallpaper. They sit on the person's appearance
 * (lib/appearance.ts, saved for all their devices as `themePack`): while a pack is on, its wallpaper and bubbles
 * stand in for the person's own choices (kept, and back as soon as the pack is turned off) and, for "the whole
 * app", its colours replace the app's tokens (styles/tokens.css). A chat's own look still comes first.
 *
 * Everything here is drawn in code (SVG and gradients): no images are downloaded. Every pack is checked for WCAG
 * AA contrast in both modes (themePacks.test.ts).
 */

export interface PackPalette {
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  text: string;
  muted: string;
  /** Buttons and links (white text on it). */
  accent: string;
  mine: string;
  mineText: string;
  theirs: string;
  theirsText: string;
  /** The wallpaper: a soft gradient with the pack's drawing on top. */
  wallFrom: string;
  wallTo: string;
  ink: string;
  inkOpacity: number;
}

type Motif = 'stars' | 'tulips' | 'balloons' | 'waves' | 'leaves' | 'dunes' | 'roses' | 'hex';

export interface ThemePack {
  id: ThemePackId;
  motif: Motif;
  light: PackPalette;
  dark: PackPalette;
}

export const THEME_PACKS: readonly ThemePack[] = [
  {
    id: 'gece-mavisi',
    motif: 'stars',
    light: { bg: '#eef2fb', surface: '#ffffff', surface2: '#e3e9f6', border: '#cdd6ea', text: '#0f1b3d', muted: '#4a5878', accent: '#1e4fd6', mine: '#dbe6ff', mineText: '#0f1b3d', theirs: '#ffffff', theirsText: '#0f1b3d', wallFrom: '#e9eefb', wallTo: '#d9e2f7', ink: '#1e3a8a', inkOpacity: 0.16 },
    dark: { bg: '#070d1f', surface: '#0e1730', surface2: '#16213f', border: '#24325a', text: '#e6ecff', muted: '#9eabd0', accent: '#3a66e0', mine: '#1f3a8a', mineText: '#eef3ff', theirs: '#16213f', theirsText: '#e6ecff', wallFrom: '#0a1228', wallTo: '#141c45', ink: '#c7d2fe', inkOpacity: 0.2 },
  },
  {
    id: 'lale',
    motif: 'tulips',
    light: { bg: '#fdf1f3', surface: '#ffffff', surface2: '#f8e3e8', border: '#efc9d2', text: '#3a0d18', muted: '#7a3b4b', accent: '#c21e4a', mine: '#ffdbe3', mineText: '#3a0d18', theirs: '#ffffff', theirsText: '#3a0d18', wallFrom: '#fdeef1', wallTo: '#f9dde4', ink: '#c21e4a', inkOpacity: 0.14 },
    dark: { bg: '#1a0a0f', surface: '#261117', surface2: '#33171f', border: '#4a2430', text: '#fde8ee', muted: '#d39aab', accent: '#d0325d', mine: '#7a1d3a', mineText: '#fff0f4', theirs: '#33171f', theirsText: '#fde8ee', wallFrom: '#1f0c12', wallTo: '#2e1019', ink: '#f9a8c0', inkOpacity: 0.14 },
  },
  {
    id: 'kapadokya',
    motif: 'balloons',
    light: { bg: '#faf3ec', surface: '#fffdfa', surface2: '#f2e6d9', border: '#e4d2bf', text: '#3b2416', muted: '#6f5240', accent: '#b4471f', mine: '#fbe0cf', mineText: '#3b2416', theirs: '#fffdfa', theirsText: '#3b2416', wallFrom: '#fbefe3', wallTo: '#f3dcc6', ink: '#a8461f', inkOpacity: 0.16 },
    dark: { bg: '#1b120c', surface: '#261a12', surface2: '#33241a', border: '#4a3526', text: '#f7e9dc', muted: '#c9ab92', accent: '#c0512a', mine: '#6e3218', mineText: '#fff1e6', theirs: '#33241a', theirsText: '#f7e9dc', wallFrom: '#20160f', wallTo: '#2f1d14', ink: '#f2b48a', inkOpacity: 0.14 },
  },
  {
    id: 'ege',
    motif: 'waves',
    light: { bg: '#eef8fa', surface: '#ffffff', surface2: '#ddf0f4', border: '#c3e1e8', text: '#06303b', muted: '#3c6470', accent: '#0b7285', mine: '#d3f1f6', mineText: '#06303b', theirs: '#ffffff', theirsText: '#06303b', wallFrom: '#eaf7fa', wallTo: '#d2edf3', ink: '#0b7285', inkOpacity: 0.15 },
    dark: { bg: '#051a20', surface: '#0a252d', surface2: '#10313b', border: '#1c4652', text: '#e1f6fa', muted: '#8fc3cf', accent: '#0f8196', mine: '#0e5866', mineText: '#e9fbff', theirs: '#10313b', theirsText: '#e1f6fa', wallFrom: '#061f26', wallTo: '#0a2d38', ink: '#7dd3e3', inkOpacity: 0.15 },
  },
  {
    id: 'orman',
    motif: 'leaves',
    light: { bg: '#f1f6f0', surface: '#ffffff', surface2: '#e2ede0', border: '#c9dcc5', text: '#13281a', muted: '#48604d', accent: '#2f7a3e', mine: '#d9eed5', mineText: '#13281a', theirs: '#ffffff', theirsText: '#13281a', wallFrom: '#eef5ec', wallTo: '#dbead7', ink: '#2f6b3a', inkOpacity: 0.15 },
    dark: { bg: '#0a140d', surface: '#111e15', surface2: '#182a1d', border: '#25402c', text: '#e3f2e5', muted: '#9cbea3', accent: '#2e8045', mine: '#1f5530', mineText: '#eefaf0', theirs: '#182a1d', theirsText: '#e3f2e5', wallFrom: '#0c1810', wallTo: '#13261a', ink: '#86c69a', inkOpacity: 0.14 },
  },
  {
    id: 'kum',
    motif: 'dunes',
    light: { bg: '#faf6ee', surface: '#fffdf8', surface2: '#f2ebdc', border: '#e3d7bf', text: '#33281a', muted: '#6a5a42', accent: '#8a5a00', mine: '#f5e6c4', mineText: '#33281a', theirs: '#fffdf8', theirsText: '#33281a', wallFrom: '#faf3e4', wallTo: '#f0e2c6', ink: '#a07634', inkOpacity: 0.18 },
    dark: { bg: '#17130c', surface: '#221c12', surface2: '#2e2618', border: '#463a25', text: '#f6eedf', muted: '#c8b694', accent: '#956612', mine: '#5c4518', mineText: '#fff6e4', theirs: '#2e2618', theirsText: '#f6eedf', wallFrom: '#1b160e', wallTo: '#2a2113', ink: '#e7c886', inkOpacity: 0.14 },
  },
  {
    id: 'gul-kurusu',
    motif: 'roses',
    light: { bg: '#f9f1f1', surface: '#fffbfb', surface2: '#f1e3e3', border: '#e3cccc', text: '#3a2226', muted: '#6e5156', accent: '#9e4f5c', mine: '#f3dcdf', mineText: '#3a2226', theirs: '#fffbfb', theirsText: '#3a2226', wallFrom: '#f8eded', wallTo: '#efdcdc', ink: '#a85d6a', inkOpacity: 0.16 },
    dark: { bg: '#1a1213', surface: '#251a1c', surface2: '#312326', border: '#4a3539', text: '#f6e8ea', muted: '#cfaeb3', accent: '#a8566a', mine: '#5e3540', mineText: '#fff1f3', theirs: '#312326', theirsText: '#f6e8ea', wallFrom: '#1e1517', wallTo: '#2b1c20', ink: '#e8b4bd', inkOpacity: 0.14 },
  },
  {
    id: 'grafit',
    motif: 'hex',
    light: { bg: '#f2f3f5', surface: '#ffffff', surface2: '#e6e8ec', border: '#d1d5db', text: '#16181d', muted: '#4b5260', accent: '#374151', mine: '#e1e4ea', mineText: '#16181d', theirs: '#ffffff', theirsText: '#16181d', wallFrom: '#eff0f3', wallTo: '#e2e5ea', ink: '#4b5563', inkOpacity: 0.14 },
    dark: { bg: '#0d0e10', surface: '#16181c', surface2: '#1f2227', border: '#2f333a', text: '#eceef2', muted: '#a3a9b4', accent: '#646d7c', mine: '#33373f', mineText: '#f4f5f7', theirs: '#1f2227', theirsText: '#eceef2', wallFrom: '#111316', wallTo: '#1a1d22', ink: '#9ca3af', inkOpacity: 0.12 },
  },
];

export const packById = (id: string | null | undefined): ThemePack | null => THEME_PACKS.find((p) => p.id === id) ?? null;

/** The pack a look uses, when it is one this app knows. */
export function activePack(look: { themePack?: ThemePackChoice | null }): { pack: ThemePack; scope: ThemePackScope } | null {
  const pack = packById(look.themePack?.id);
  return pack ? { pack, scope: look.themePack?.scope ?? 'app' } : null;
}

// ---------- colours ----------

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2 contrast ratio of two #rrggbb colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
/** `share` of a over b, as CSS color-mix(in srgb, a share, b) gives it. */
export function mixHex(a: string, b: string, share: number): string {
  return `#${[1, 3, 5]
    .map((i) => Math.round(parseInt(a.slice(i, i + 2), 16) * share + parseInt(b.slice(i, i + 2), 16) * (1 - share)).toString(16).padStart(2, '0'))
    .join('')}`;
}
/** The focus ring: the accent in light mode, a lighter accent on dark surfaces. */
export const focusOf = (p: PackPalette, dark: boolean) => (dark ? mixHex('#ffffff', p.accent, 0.45) : p.accent);

// ---------- wallpapers ----------

const TILE: Record<Motif, [number, number]> = { stars: [160, 160], tulips: [160, 160], balloons: [160, 160], waves: [160, 80], leaves: [160, 160], dunes: [160, 120], roses: [160, 160], hex: [56, 98] };

/** Each motif as SVG shapes, `F` filled and `S` stroked in the pack's ink. */
function motifShapes(m: Motif): { fill?: string; stroke?: string } {
  switch (m) {
    case 'stars': {
      const star = (x: number, y: number, r: number) => `M${x} ${y - r}L${x + r * 0.25} ${y - r * 0.25}L${x + r} ${y}L${x + r * 0.25} ${y + r * 0.25}L${x} ${y + r}L${x - r * 0.25} ${y + r * 0.25}L${x - r} ${y}L${x - r * 0.25} ${y - r * 0.25}Z`;
      const dot = (x: number, y: number, r: number) => `M${x - r} ${y}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0Z`;
      return {
        fill: [star(28, 30, 7), star(104, 18, 4), star(138, 96, 6), star(54, 122, 4), star(88, 70, 3), dot(70, 34, 1.6), dot(16, 84, 1.4), dot(124, 140, 1.8), dot(150, 40, 1.2), dot(36, 150, 1.2), 'M112 104a16 16 0 1 0 14 26a12 12 0 1 1 -14 -26Z'].join(''),
      };
    }
    case 'tulips': {
      const tulip = (x: number, y: number) => `M${x} ${y + 20}c-9-2-13-12-11-23c4 4 7 6 11 6s7-2 11-6c2 11-2 21-11 23Z`;
      const stem = (x: number, y: number) => `M${x} ${y + 20}v24M${x} ${y + 36}c-7-1-11-7-12-14M${x} ${y + 32}c6-1 10-6 11-12`;
      return { fill: tulip(40, 18) + tulip(120, 94), stroke: stem(40, 18) + stem(120, 94) + 'M96 30c4-4 10-4 14 0M20 120c4-4 10-4 14 0' };
    }
    case 'balloons': {
      const balloon = (x: number, y: number, s: number) =>
        `M${x} ${y}c${-10 * s} 0 ${-16 * s} ${8 * s} ${-16 * s} ${16 * s}c0 ${9 * s} ${9 * s} ${16 * s} ${14 * s} ${22 * s}h${4 * s}c${5 * s}-${6 * s} ${14 * s}-${13 * s} ${14 * s}-${22 * s}c0-${8 * s}-${6 * s}-${16 * s}-${16 * s}-${16 * s}ZM${x - 3 * s} ${y + 42 * s}h${6 * s}v${5 * s}h${-6 * s}Z`;
      const chimney = (x: number, y: number) => `M${x - 10} ${y}l7-26c1-5 7-5 8 0l7 26Z`;
      return { fill: balloon(40, 16, 1) + balloon(122, 62, 0.75) + chimney(98, 152) + chimney(118, 152) + chimney(20, 156), stroke: 'M0 156c30-6 50-6 80 0s50 6 80 0' };
    }
    case 'waves':
      return { stroke: 'M0 22q20-12 40 0t40 0t40 0t40 0M0 62q20-12 40 0t40 0t40 0t40 0', fill: 'M126 36a6 6 0 1 0 12 0a6 6 0 1 0 -12 0Z' };
    case 'leaves': {
      const leaf = (x: number, y: number, a: number) => `<g transform="translate(${x} ${y}) rotate(${a})"><path d="M0 0c10-11 26-11 34 0c-8 11-24 11-34 0ZM3 0h28M14 0l6-5M20 0l6 5"/></g>`;
      return { stroke: leaf(18, 34, -24) + leaf(96, 24, 18) + leaf(60, 98, -8) + leaf(122, 128, 34) + leaf(10, 136, 12) };
    }
    case 'dunes':
      return { stroke: 'M0 46q40-26 80 0t80 0M0 100q40-26 80 0t80 0M20 72q20-10 40 0', fill: 'M118 20a8 8 0 1 0 16 0a8 8 0 1 0 -16 0Z' };
    case 'roses': {
      const rose = (x: number, y: number) => `M${x - 12} ${y}a12 12 0 1 0 24 0a12 12 0 1 0 -24 0M${x - 6} ${y}a6 6 0 1 1 7 6M${x + 3} ${y - 3}a3 3 0 1 0 -4 3M${x - 12} ${y + 4}c-6 6-6 14-2 18M${x + 12} ${y + 4}c6 6 6 14 2 18`;
      const petal = (x: number, y: number) => `M${x} ${y}c4-6 12-6 12 0c0 6-8 8-12 0Z`;
      return { stroke: rose(40, 40) + rose(118, 112), fill: petal(110, 30) + petal(30, 118) + petal(78, 76) };
    }
    case 'hex':
      return { stroke: 'M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 98' };
  }
}

function motifSvg(m: Motif, ink: string, opacity: number): string {
  const [w, h] = TILE[m];
  const { fill, stroke } = motifShapes(m);
  const strokePart = stroke?.startsWith('<g')
    ? `<g fill="none" stroke="${ink}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${stroke}</g>`
    : stroke
      ? `<path d="${stroke}" fill="none" stroke="${ink}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`
      : '';
  const fillPart = fill ? `<path d="${fill}" fill="${ink}"/>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><g opacity="${opacity}">${fillPart}${strokePart}</g></svg>`;
}

export interface PackWallpaper {
  image: string;
  size: string;
  color: string;
}
/** A pack's wallpaper in one mode, as CSS background values. */
export function packWallpaper(pack: ThemePack, dark: boolean): PackWallpaper {
  const p = dark ? pack.dark : pack.light;
  const [w, h] = TILE[pack.motif];
  const svg = `url("data:image/svg+xml,${encodeURIComponent(motifSvg(pack.motif, p.ink, p.inkOpacity))}")`;
  return { image: `${svg}, linear-gradient(160deg, ${p.wallFrom} 0%, ${p.wallTo} 100%)`, size: `${w}px ${h}px, cover`, color: p.wallFrom };
}

// ---------- CSS ----------

/** The chat variables chatStyle() points at while a pack is on. */
export const PACK_CHAT_VARS = {
  '--chat-bg-color': 'var(--pack-chat-bg-color)',
  '--chat-bg-image': 'var(--pack-chat-bg-image)',
  '--chat-bg-size': 'var(--pack-chat-bg-size)',
} as CSSProperties;
/** The pack's dark wallpaper is drawn dark already (no dimming). */
export const PACK_CHAT_DIM = '0%';
export const PACK_MINE_VARS = { '--bubble-mine-bg': 'var(--pack-bubble-mine-bg)', '--bubble-mine-fg': 'var(--pack-bubble-mine-fg)' } as CSSProperties;
export const PACK_THEIRS_VARS = { '--bubble-theirs-bg': 'var(--pack-bubble-theirs-bg)', '--bubble-theirs-fg': 'var(--pack-bubble-theirs-fg)' } as CSSProperties;

/** The app's tokens for a pack in one mode (also used by the gallery's previews). */
export function appTokens(p: PackPalette, dark: boolean): Record<string, string> {
  return {
    '--bg': p.bg,
    '--surface': p.surface,
    '--surface-2': p.surface2,
    '--border': p.border,
    '--text': p.text,
    '--text-muted': p.muted,
    '--accent': p.accent,
    '--accent-soft': `color-mix(in srgb, ${p.accent} ${dark ? 24 : 14}%, transparent)`,
    '--focus': focusOf(p, dark),
  };
}

function chatTokens(pack: ThemePack, dark: boolean): Record<string, string> {
  const p = dark ? pack.dark : pack.light;
  const wall = packWallpaper(pack, dark);
  return {
    '--pack-chat-bg-color': wall.color,
    '--pack-chat-bg-image': wall.image,
    '--pack-chat-bg-size': wall.size,
    '--pack-bubble-mine-bg': p.mine,
    '--pack-bubble-mine-fg': p.mineText,
    '--pack-bubble-theirs-bg': p.theirs,
    '--pack-bubble-theirs-fg': p.theirsText,
  };
}

const block = (vars: Record<string, string>) =>
  Object.entries(vars)
    .map(([k, v]) => `${k}:${v};`)
    .join('');

/** The stylesheet that puts a pack on the page (light, chosen dark, and the system's dark). */
export function packCss(pack: ThemePack, scope: ThemePackScope): string {
  const vars = (dark: boolean) => block({ ...(scope === 'app' ? appTokens(dark ? pack.dark : pack.light, dark) : {}), ...chatTokens(pack, dark) });
  // More specific than tokens.css's own light, dark and system-dark rules, so the pack wins in each.
  return [
    `:root[data-pack]{${vars(false)}}`,
    `:root[data-pack][data-theme='dark']{${vars(true)}}`,
    `@media (prefers-color-scheme: dark){:root[data-pack]:not([data-theme='light']):not([data-theme='dark']){${vars(true)}}}`,
  ].join('\n');
}

const STYLE_ID = 'da-theme-pack';

/** Puts the chosen pack on the page, or takes it off; true while one is on. */
export function applyThemePack(choice: ThemePackChoice | null | undefined): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  const pack = packById(choice?.id);
  let el = document.getElementById(STYLE_ID);
  if (!pack || !choice) {
    el?.remove();
    delete root.dataset.pack;
    delete root.dataset.packScope;
    return false;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  const css = packCss(pack, choice.scope);
  if (el.textContent !== css) el.textContent = css;
  root.dataset.pack = pack.id;
  root.dataset.packScope = choice.scope;
  return true;
}
